import React, { useEffect, useState } from 'react';
import PageHeader from '../../PageHeader/PageHeader';
import { HitApi } from '../../Api/ApiHit';
import { searchFeedback, deleteFeedback } from '../../constant/Constant';
import { useDispatch, useSelector } from 'react-redux';
import { setApiJson } from '../../redux/Actions/ApiAction';
import AppTable from '../../components/AppTable/AppTable';
import { setPagination } from '../../redux/Actions/TableAction';
import toast from 'react-hot-toast';
import TableSearch from '../../components/TableSearch/TableSearch';
import DeleteButton from '../../components/Delete/DeleteButton';

const EMOJI_MAP = {
    1: { emoji: '😡', label: 'Very Bad', color: 'bg-red-100 text-red-800' },
    2: { emoji: '😞', label: 'Bad', color: 'bg-orange-100 text-orange-800' },
    3: { emoji: '😐', label: 'Okay', color: 'bg-yellow-100 text-yellow-800' },
    4: { emoji: '🙂', label: 'Good', color: 'bg-green-100 text-green-800' },
    5: { emoji: '😄', label: 'Excellent', color: 'bg-emerald-100 text-emerald-800' },
};

function ClientFeedback() {
    const dispatch = useDispatch();
    const ApiReducer = useSelector((state) => state.ApiReducer);
    const TableDataReducer = useSelector((state) => state.TableReducer);
    const [optionSelect, setOptionSelect] = useState('');
    const [valueSearched, setValueSearched] = useState('');
    const [isSearching, setIsSearching] = useState(false);

    useEffect(() => {
        fetchFeedbacks();
    }, [TableDataReducer.pagination.page, TableDataReducer.pagination.limit]);

    const fetchFeedbacks = (searchKey, searchValue) => {
        setIsSearching(true);

        const searchObj = {};
        if (searchKey && searchValue) {
            searchObj[searchKey] = searchValue;
        }

        const json = {
            page: TableDataReducer.pagination.page,
            limit: TableDataReducer.pagination.limit,
            search: searchObj
        };

        HitApi(json, searchFeedback).then((res) => {
            if (res?.statusCode === 200) {
                const feedbackData = Array.isArray(res?.data?.docs) ? res?.data?.docs : [];
                dispatch(setApiJson(feedbackData));

                const paginationData = {
                    total: res?.data?.totalDocs || 0,
                    page: TableDataReducer.pagination.page,
                    limit: TableDataReducer.pagination.limit
                };
                dispatch(setPagination(paginationData));
            } else {
                dispatch(setApiJson([]));
                toast.error('Failed to fetch feedbacks.');
            }
        }).catch(() => {
            dispatch(setApiJson([]));
            toast.error('Error fetching feedbacks.');
        }).finally(() => {
            setIsSearching(false);
        });
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const renderRating = (rating) => {
        const ratingInfo = EMOJI_MAP[rating] || { emoji: '—', label: 'N/A', color: 'bg-gray-100 text-gray-600' };
        return (
            <div className="flex items-center gap-2">
                <span className="text-2xl">{ratingInfo.emoji}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ratingInfo.color}`}>
                    {ratingInfo.label}
                </span>
            </div>
        );
    };

    const handleSearch = () => {
        if (!optionSelect) {
            toast.error('Please select a search option');
            return;
        }
        if (!valueSearched || valueSearched.trim() === '') {
            toast.error('Please enter a search value');
            return;
        }
        fetchFeedbacks(optionSelect, valueSearched.trim());
    };

    const handleClearSearch = () => {
        setOptionSelect('');
        setValueSearched('');
        fetchFeedbacks();
    };

    const handleDelete = (row) => {
        if (window.confirm(`Are you sure you want to delete this feedback from ${row.clientName}?`)) {
            const json = { _id: row?._id };
            HitApi(json, deleteFeedback).then((res) => {
                if (res?.statusCode === 200) {
                    toast.success('Feedback deleted successfully!');
                    fetchFeedbacks();
                } else {
                    toast.error('Failed to delete feedback.');
                }
            }).catch(() => {
                toast.error('Error deleting feedback.');
            });
        }
    };

    const options = [
        { label: 'Client Name', value: 'clientName' },
        { label: 'Service', value: 'service' },
    ];

    const baseIndex = (TableDataReducer.pagination.page - 1) * TableDataReducer.pagination.limit;

    const tableHeaders = [
        {
            title: "S.No",
            key: "serialNumber",
            width: "60px",
            align: "center",
            render: (_, row, index) => {
                const serialNumber = baseIndex + index + 1;
                return (
                    <div className="text-center">
                        <span className="text-sm font-medium text-gray-900">{serialNumber}</span>
                    </div>
                );
            }
        },
        {
            title: "Client Name",
            key: "clientName",
            render: (value) => (
                <div className="flex items-center">
                    <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center mr-3">
                        <span className="text-sm font-medium text-gray-600">
                            {value && value.charAt(0).toUpperCase()}
                        </span>
                    </div>
                    <div className="font-medium text-gray-900">{value || 'N/A'}</div>
                </div>
            )
        },
        {
            title: "Service",
            key: "service",
            render: (value) => (
                <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {value || 'N/A'}
                </span>
            )
        },
        {
            title: "Service Rating",
            key: "serviceRating",
            align: "center",
            render: (value) => renderRating(value)
        },
        {
            title: "Overall Rating",
            key: "overallRating",
            align: "center",
            render: (value) => renderRating(value)
        },
        {
            title: "Comment",
            key: "comment",
            render: (value) => (
                <div className="max-w-48">
                    <span className="text-sm text-gray-700 line-clamp-2" title={value}>
                        {value || '—'}
                    </span>
                </div>
            )
        },
        {
            title: "Date",
            key: "createdAt",
            render: (value) => (
                <span className="text-sm text-gray-600">{formatDate(value)}</span>
            )
        },
        {
            title: "Actions",
            key: "actions",
            align: "center",
            render: (_, row) => (
                <div className="flex justify-center">
                    <DeleteButton
                        onDelete={() => handleDelete(row)}
                        itemName="feedback"
                        tooltip="Delete Feedback"
                    />
                </div>
            )
        },
    ];

    return (
        <div className="p-5">
            <PageHeader
                title={'Client Feedback'}
                description={'View all client feedbacks submitted via QR code'}
            />

            <AppTable
                TH={tableHeaders}
                TD={Array.isArray(ApiReducer.apiJson) ? ApiReducer.apiJson : []}
                isLoading={!Array.isArray(ApiReducer.apiJson) || ApiReducer.apiJson.length === 0 || isSearching}
                emptyMessage="No feedbacks found"
                TableSearch={
                    <TableSearch
                        options={options}
                        onClick={handleSearch}
                        optionSelect={optionSelect}
                        setOptionSelect={setOptionSelect}
                        setValueSearched={setValueSearched}
                        valueSearched={valueSearched}
                        onClear={handleClearSearch}
                    />
                }
            />
        </div>
    );
}

export default ClientFeedback;
